import { AxiosError } from 'axios';

import apiClient from '../lib/apiClient';
import {
  PaymentListOrderBy,
  PaymentStatus,
} from '../hooks/queries/usePaymentList';

import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '../constants/paymentList ';
import { PAYMENTS_API_ENDPOINT } from '../constants/apiEndpoints';

export interface IPayment {
  id: number;
  patientName: string;
  amount: string;
  paymentIntentId: string;
  status: string;
  paymentMethodType: string;
  invoiceNumber: string;
  billingDate: string;
  description: string;
  items: {
    id: number;
    description: string;
  }[];
}

export interface IGetPaymentListResponseData {
  success: boolean;
  data: {
    payments: {
      id: number;
      patient_full_name: string;
      amount: string;
      payment_intent_id: string;
      status: string;
      payment_method: string;
      invoice_number: string;
      billing_date: string;
      description: string;
      items: {
        id: number;
        description: string;
      }[];
    }[];
    total_pages: number;
    total_records: number;
    all_payment_count: number;
    paid_payment_count: number;
    unpaid_payment_count: number;
  };
  message: string;
}

export interface IGetPaymentListParams {
  search?: string;
  status?: PaymentStatus | string;
  orderBy?: PaymentListOrderBy;
  orderDirection?: 'asc' | 'desc';
  pageNumber?: number;
  perPage?: number;
  fromDate?: Date;
  toDate?: Date;
  patientId?: number;
}

const mapApiPaymentToPayment = (
  apiPayment: IGetPaymentListResponseData['data']['payments'][0]
): IPayment => ({
  id: apiPayment.id,
  patientName: apiPayment.patient_full_name,
  amount: String(parseFloat(apiPayment.amount)),
  paymentIntentId: apiPayment.payment_intent_id,
  status: apiPayment.status,
  paymentMethodType: apiPayment.payment_method,
  invoiceNumber: apiPayment.invoice_number,
  billingDate: apiPayment.billing_date,
  description: apiPayment.description,
  items: apiPayment.items,
});

export async function getPaymentList(
  params: IGetPaymentListParams = {}
): Promise<{
  payments: IPayment[];
  totalPages: number;
  totalRecords: number;
  message: string;
  success: boolean;
  all_payment_count: number;
  paid_payment_count: number;
  unpaid_payment_count: number;
}> {
  const {
    search,
    status,
    orderBy,
    orderDirection,
    pageNumber = DEFAULT_PAGE_NUMBER,
    perPage = DEFAULT_PAGE_SIZE,
    fromDate,
    toDate,
    patientId,
  } = params;

  try {
    const response = await apiClient.get<IGetPaymentListResponseData>(
      PAYMENTS_API_ENDPOINT,
      {
        params: {
          search,
          status,
          order_by: orderBy,
          order_direction: orderDirection,
          page_no: pageNumber,
          per_page: perPage,
          from_date: fromDate?.toISOString(),
          to_date: toDate?.toISOString(),
          patient_id: patientId,
        },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch payments');
    }

    const responseData = response.data.data;
    const responsePayments = responseData?.payments || [];

    return {
      success: response.data.success,
      message: response.data.message,
      payments: responsePayments.map(mapApiPaymentToPayment),
      totalPages: responseData.total_pages,
      totalRecords: responseData.total_records,
      all_payment_count: responseData.all_payment_count,
      paid_payment_count: responseData.paid_payment_count,
      unpaid_payment_count: responseData.unpaid_payment_count,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response) {
        throw new Error(
          `Payment list request failed with status ${error.response.status}: ${
            error.response.data?.message || error.message
          }`
        );
      } else if (error.request) {
        throw new Error('No response received from server');
      }
    }

    throw new Error('Failed to fetch payments: Unknown error occurred');
  }
}
